import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartData } from 'chart.js';
import { TransportRegisterByCategoryResponse } from '../api/types';
import styles from './styles.module.css';
import { SelectYear } from '../../../../components/selects';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface TrailedEquipmentProps {
  categoryData?: TransportRegisterByCategoryResponse;
}

interface CategoryData {
  agriculturalMachineryTypeQuantityAnalytics: Array<{
    transportType: {
      nameRu: string;
      nameKy: string;
      alias: string;
      id: number;
      category: {
        id: number;
        alias: string;
        nameRu: string;
        nameKy: string;
      };
    };
    transportTypeQuantity: number;
  }>;
  category: {
    alias: string;
    id: number;
    nameRu: string;
    nameKy: string;
  };
  totalQuantity: number;
}

const CATEGORY_ORDER = [
  'mounted_implements',
  'harvesters',
  'special_agricultural_tech',
  'tractor'
];

const CHART_COLORS = {
  'mounted_implements': [
    'rgba(0, 81, 0, 1)',    // Зеленый
    'rgb(74, 222, 128)',   // Светло-зеленый
    'rgb(134, 239, 172)',  // Еще светлее зеленый
    'rgb(187, 247, 208)',  // Очень светлый зеленый
    'rgb(220, 252, 231)',  // Почти белый зеленый
    'rgb(254, 249, 195)',  // Светло-желтый
    'rgb(253, 224, 71)',   // Желтый
    'rgb(250, 204, 21)',   // Насыщенный желтый
    'rgb(251, 191, 36)',   // Золотистый
    'rgb(253, 186, 116)',  // Светло-оранжевый
    'rgb(251, 146, 60)',   // Оранжевый
    'rgb(249, 115, 22)',   // Темно-оранжевый
  ],
  'harvesters': [
    'rgba(0, 81, 0, 1)',    // Зеленый
    'rgb(134, 239, 172)',  // Светло-зеленый
    'rgb(253, 224, 71)',   // Желтый
    'rgb(251, 146, 60)',   // Оранжевый
  ],
  'special_agricultural_tech': [
    'rgba(0, 81, 0, 1)',    // Зеленый
    'rgb(134, 239, 172)',  // Светло-зеленый
    'rgb(253, 224, 71)',   // Желтый
  ],
  'tractor': [
    'rgba(0, 81, 0, 1)',    // Зеленый
  ]
};

export const TrailedEquipment: React.FC<TrailedEquipmentProps> = ({ categoryData }) => {
  const [selectedYear, setSelectedYear] = useState<number | null>(2025);

  useEffect(() => {
    if (categoryData?.res && categoryData.res.length > 0) {
      if (!selectedYear) {
        setSelectedYear(categoryData.res[categoryData.res.length - 1].year);
      }
    }
  }, [categoryData]);

  const processChartData = (category: CategoryData): ChartData<'bar'> => {
    if (!category?.agriculturalMachineryTypeQuantityAnalytics) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 1,
        }],
      };
    }

    const types = category.agriculturalMachineryTypeQuantityAnalytics
      .filter(item => item.transportType && item.transportTypeQuantity >= 0)
      .sort((a, b) => b.transportTypeQuantity - a.transportTypeQuantity);

    const categoryColors = CHART_COLORS[category.category.alias as keyof typeof CHART_COLORS] || CHART_COLORS.mounted_implements;

    return {
      labels: types.map(type => type.transportType.nameRu),
      datasets: [
        {
          data: types.map(type => type.transportTypeQuantity),
          backgroundColor: types.map((_, index) => categoryColors[index % categoryColors.length]),
          borderColor: '#fff',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  const getChartOptions = (categoryName: string) => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#e0e0e0',
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
  });

  const renderCategoryCharts = () => {
    if (!categoryData?.res || !selectedYear) return null;

    const yearData = categoryData.res.find(item => item.year === selectedYear);
    if (!yearData) return null;

    const sortedCategories = yearData.categories
      .filter(category => category.agriculturalMachineryTypeQuantityAnalytics.length > 0)
      .sort((a, b) => {
        const indexA = CATEGORY_ORDER.indexOf(a.category.alias);
        const indexB = CATEGORY_ORDER.indexOf(b.category.alias);
        return indexA - indexB;
      });

    return sortedCategories.map((category) => (
      <div key={category.category.id} className={styles.categoryContainer}>
        <h2 className={styles.categoryTitle}>{category.category.nameRu}</h2>
        <div className={styles.chartContainer}>
          <Bar 
            options={getChartOptions(category.category.nameRu)} 
            data={processChartData(category)} 
          />
        </div>
      </div>
    ));
  };

  const handleYearChange = (year: number | null) => {
    setSelectedYear(year);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.header}>
        <SelectYear
          value={selectedYear}
          onChange={handleYearChange}
        />
      </div>
      <div className={styles.chartsContainer}>
        {renderCategoryCharts()}
      </div>
    </div>
  );
};